/**
 * "Browse all sections" dropdown — surfaces every nav_categories item so
 * users on narrow screens (or with lots of items) don't need to horizontally
 * scroll the Explore bar to find something.
 *
 * The dropdown panel is rendered via React portal into <body> so it escapes
 * the ExploreBar's `overflow-x-auto` clip and never gets hidden behind the
 * horizontal scroll strip.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ChevronDown, Compass } from "lucide-react";
import { POLICY_LINKS } from "@/data/policies";

// Fallback list — mirrors the canonical `seed_nav` on the backend so the
// dropdown is never empty on fresh deploys or when the API call hasn't
// resolved yet. Once /api/nav-categories returns, we replace this list.
const FALLBACK_NAV = [
  { label: "Housing & Shelter", href: "/causes?category=housing" },
  { label: "Food & Basics", href: "/causes?category=food" },
  { label: "Education & Supplies", href: "/causes?category=education" },
  { label: "Medical Care & Prescription", href: "/causes?category=medical" },
  { label: "Senior Care & Childcare", href: "/causes?category=senior" },
  { label: "Community & Environment", href: "/causes?category=community" },
  { label: "Celebration of Life", href: "/celebrate" },
  { label: "Obituaries", href: "/obituaries" },
  { label: "Family Tree — How it works", href: "/family-tree-guide" },
  { label: "Memorial Services", href: "/services" },
  { label: "In Memoriam", href: "/in-memoriam" },
  { label: "Events", href: "/events" },
  { label: "Causes", href: "/causes" },
  { label: "Daily News", href: "/things-to-know" },
  { label: "Daily Recipes", href: "/recipes" },
  { label: "Stories", href: "/stories" },
  { label: "World Changers", href: "/world-changers" },
  { label: "Organizations", href: "/organizations" },
  { label: "Housing Projects", href: "/housing" },
  { label: "Impact Map", href: "/impact-map" },
  { label: "Volunteer", href: "/opportunities" },
  { label: "Find Help", href: "/find-help" },
  { label: "Offer Help", href: "/offer-help" },
];

export default function BrowseAllMenu() {
  const [items, setItems] = useState(FALLBACK_NAV);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    http.get("/nav-categories").then((r) => {
      const raw = r?.data?.items || [];
      const filtered = raw.filter((i) => i.enabled !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      // Only replace the fallback if the API actually returned items —
      // otherwise the user always sees a populated menu.
      if (filtered.length > 0) setItems(filtered);
    }).catch(() => {});
  }, []);

  // Compute panel position: pin it BELOW the entire Explore bar so nothing
  // in the horizontal-scroll strip visually clips or overlaps the panel.
  const recomputePos = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    // Find the Explore bar in the DOM and use its bottom edge.
    // Fall back to trigger.bottom + 72 if the bar isn't present.
    let anchorBottom = r.bottom + 72;
    const bar = document.querySelector('[data-testid="explore-bar"]');
    if (bar) {
      const br = bar.getBoundingClientRect();
      if (br.bottom > anchorBottom) anchorBottom = br.bottom;
    }
    setPos({
      top: anchorBottom + 8, // small breathing gap
      right: Math.max(16, window.innerWidth - r.right),
    });
  };

  useLayoutEffect(() => { if (open) recomputePos(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onResize = () => recomputePos();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        data-testid="browse-all-menu-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium hover:bg-[color:var(--wofy-bg)] transition-colors"
        style={{
          background: "var(--wofy-bg)",
          borderColor: "var(--wofy-border)",
          color: "var(--wofy-text)",
        }}
      >
        <Compass className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        Browse all sections
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          data-testid="browse-all-menu-panel"
          role="listbox"
          className="fixed w-72 max-h-[70vh] overflow-y-auto rounded-2xl border shadow-2xl z-[60]"
          style={{
            top: pos.top,
            right: pos.right,
            background: "var(--wofy-bg)",
            borderColor: "var(--wofy-border)",
          }}
        >
          <div className="px-4 py-3 border-b sticky top-0" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}>
            <div className="overline">EXPLORE</div>
            <div className="text-xs opacity-60 mt-0.5">{items.length} sections</div>
          </div>
          <ul className="py-1">
            {items.map((it) => (
              <li key={it.id || it.href}>
                <Link
                  to={it.href}
                  data-testid={`browse-all-item-${it.href.replace(/[^a-z0-9]/gi, '-')}`}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm hover:bg-[color:var(--wofy-surface)]"
                >
                  {it.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Policies section — legal links visible from every page's dropdown */}
          <div className="border-t sticky bottom-0" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}>
            <div className="px-4 py-2 overline">POLICIES</div>
            <ul className="pb-2">
              {POLICY_LINKS.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/policies/${p.slug}`}
                    data-testid={`browse-all-policy-${p.slug}`}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm hover:bg-[color:var(--wofy-surface)]"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
